export interface LegacyTransitionSnapshot {
    legacy_transition?: Record<string, unknown> | null;
}

export type LegacyTransitionStatus =
    | 'not_applicable'
    | 'grace'
    | 'expired_pending'
    | 'enforced'
    | 'converted';

export interface LegacyTransition {
    status: LegacyTransitionStatus;
    is_former_legacy: boolean;
    rss_grandfathered: boolean;
    expires_at: string | null;
    kept_project_ids: string[];
    paused_project_ids: string[];
    made_public_project_ids: string[];
    enforced_at: string | null;
    upgrade_destination: LegacyUpgradeDestination;
}

export type LegacyUpgradeDestination = 'early_user_offer' | 'pricing';

export interface LegacyUpgradeAction {
    destination: LegacyUpgradeDestination;
    path: '/early-user-offer' | '/pricing';
    label: MessageDescriptor;
    hasOffer: boolean;
}

const EMPTY_TRANSITION: LegacyTransition = {
    status: 'not_applicable',
    is_former_legacy: false,
    rss_grandfathered: false,
    expires_at: null,
    kept_project_ids: [],
    paused_project_ids: [],
    made_public_project_ids: [],
    enforced_at: null,
    upgrade_destination: 'pricing',
};

function optionalString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function stringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map(optionalString)
        .filter((item): item is string => Boolean(item));
}

export function getLegacyTransition(
    snapshot: LegacyTransitionSnapshot | null | undefined
): LegacyTransition {
    const raw = snapshot?.legacy_transition;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return EMPTY_TRANSITION;
    }

    const status = optionalString(raw.status);
    const supportedStatus = status === 'grace'
        || status === 'expired_pending'
        || status === 'enforced'
        || status === 'converted'
        ? status
        : 'not_applicable';

    const legacyKeepProjectId = optionalString(raw.keep_project_id);
    const keptProjectIds = stringArray(raw.kept_project_ids);
    const normalizedKeptProjectIds = keptProjectIds.length > 0
        ? keptProjectIds
        : legacyKeepProjectId
            ? [legacyKeepProjectId]
            : [];
    const upgradeDestination = raw.upgrade_destination === 'early_user_offer'
        ? 'early_user_offer'
        : 'pricing';

    return {
        status: supportedStatus,
        is_former_legacy: raw.is_former_legacy === true,
        rss_grandfathered: raw.rss_grandfathered === true,
        expires_at: optionalString(raw.expires_at),
        kept_project_ids: normalizedKeptProjectIds,
        paused_project_ids: stringArray(raw.paused_project_ids),
        made_public_project_ids: stringArray(raw.made_public_project_ids),
        enforced_at: optionalString(raw.enforced_at),
        upgrade_destination: upgradeDestination,
    };
}

export function isLegacyAccessEnded(
    snapshot: LegacyTransitionSnapshot | null | undefined
): boolean {
    const status = getLegacyTransition(snapshot).status;
    return status === 'expired_pending' || status === 'enforced';
}

export function getLegacyPausedProjectIds(
    snapshot: LegacyTransitionSnapshot | null | undefined
): Set<string> {
    return new Set(getLegacyTransition(snapshot).paused_project_ids);
}

export function getLegacyUpgradeAction(
    snapshot: LegacyTransitionSnapshot | null | undefined
): LegacyUpgradeAction {
    const destination = getLegacyTransition(snapshot).upgrade_destination;
    const hasOffer = destination === 'early_user_offer';

    return {
        destination,
        path: hasOffer ? '/early-user-offer' : '/pricing',
        label: hasOffer
            ? msg({ id: 'billing.viewEarlyUserOffer', message: 'View early-user offer' })
            : msg({ id: 'billing.viewPlans', message: 'View plans' }),
        hasOffer,
    };
}
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
